import React, { useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import {
  Button,
  BUTTON_THEME,
  CheckBox,
  Chip,
  Collapsible,
  Icon,
  Input,
  Modal,
  Select,
} from '@st-operations/uikit/src/v2'

import capitalizeFirstLetter from '_lib/captalizeFirstLetter'
import useTrendingTerms from '../hooks/useTrendingTerms'
import twoNonZeroDecimals from '_lib/twoNonZeroDecimals'
import { useRequestConfig } from '_hooks'
import { useEventFeedsContext } from '../../../context'
import RectLoader from '_components/rect-loader/index.jsx'
import LookbackSelectStored from '_components/lookback-select-stored/index.jsx'
import { TYPES } from '../index.jsx'
import { UIKIT_COLORS } from '../../scale-and-coverage/constants'
import extractCategoryTree from '../../../categories/extractCategoryTree'
import useCategoryUsers from '../../../wizard/hooks/useCategoryUsers'

import './styles.scss'

export const ENTITY_TYPES = {
  ALL_TYPES: '',
  BROADCASTER: 'broadcaster',
  COMPANY: 'company',
  FACILITY: 'facility',
  HEALTH_CONDITIONS: 'health_conditions',
  LOCATION: 'location',
  MOVIE: 'movie',
  ORGANIZATION: 'organization',
  PERSON: 'person',
  SPORT: 'sport',
  SPORTING_EVENT: 'sporting_event',
  TELEVISION_SHOW: 'television_show',
  VEHICLE: 'vehicle',
}

const AddTermsModal = ({ isOpen, onSaveChangesClick, field, onFieldChange, onClose }) => {
  return (
    <Modal
      isOpen={isOpen}
      size="large"
      classes={{
        header: 'add-terms-modal__header',
        headerClose: 'add-terms-modal__header-close',
        content: 'add-terms-modal__content',
        innerWrapper: 'add-terms-modal__w-full add-terms-modal__h-full',
        childWrapper: 'add-terms-modal__h-full',
      }}
      onClose={onClose}
    >
      <MainPage
        onSaveChangesClick={onSaveChangesClick}
        field={field}
        onFieldChange={onFieldChange}
      />
    </Modal>
  )
}

const MainPage = ({ onSaveChangesClick, isOpen, field, onFieldChange }) => {
  const {
    data: { filters },
  } = useEventFeedsContext()

  const [entityType, setEntityType] = useState(ENTITY_TYPES.ALL_TYPES)
  const [trendingSince, setTrendingSince] = useState()
  const [selectedTerms, setSelectedTerms] = useState([])
  const [categorySelectedTerms, setCategorySelectedTerms] = useState([])
  const [conceptSelectedTerms, setConceptSelectedTerms] = useState([])
  const [domainSelectedTerms, setDomainSelectedTerms] = useState([])
  const [entitySelectedTerms, setEntitySelectedTerms] = useState([])
  const [keywordSelectedTerms, setKeywordSelectedTerms] = useState([])
  const [englishLanguage, setEnglishLanguage] = useState(true)
  const [moreTerms, setMoreTerms] = useState()
  const [categoriesSearch, setCategoriesSearch] = useState('')

  const [selectedLevel1Category, setSelectedLevel1Category] = useState()

  const payload = useMemo(() => {
    return field === TYPES.CATEGORIES
      ? {
          filters,
        }
      : undefined
  }, [selectedTerms])
  const [categoryUsers] = useCategoryUsers(payload)

  const extractedCategoryTree = useMemo(
    () => extractCategoryTree(categoryUsers?.categories || [], ' users'),
    [categoryUsers]
  )

  useEffect(() => {
    setSelectedLevel1Category(extractedCategoryTree[0].value)
  }, [])

  const { terms: categoryTerms, lookback: categoryLookback } = useRequestConfig(
    `trends.${TYPES.CATEGORIES}`
  )
  const { terms: conceptTerms, lookback: conceptLookback } = useRequestConfig(
    `trends.${TYPES.CONCEPTS}`
  )
  const { terms: domainTerms, lookback: domainLookback } = useRequestConfig(
    `trends.${TYPES.DOMAINS}`
  )
  const { terms: entityTerms, lookback: entityLookback } = useRequestConfig(
    `trends.${TYPES.ENTITIES}`
  )
  const { terms: keywordTerms, lookback: keywordLookback } = useRequestConfig(
    `trends.${TYPES.KEYWORDS}`
  )

  useEffect(() => {
    switch (field) {
      case TYPES.CATEGORIES: {
        setSelectedTerms(categorySelectedTerms)
        setTrendingSince(categoryLookback)
        break
      }
      case TYPES.CONCEPTS: {
        setSelectedTerms(conceptSelectedTerms)
        setTrendingSince(conceptLookback)
        break
      }
      case TYPES.DOMAINS: {
        setSelectedTerms(domainSelectedTerms)
        setTrendingSince(domainLookback)
        break
      }
      case TYPES.ENTITIES: {
        setSelectedTerms(entitySelectedTerms)
        setTrendingSince(entityLookback)
        break
      }
      case TYPES.KEYWORDS: {
        setSelectedTerms(keywordSelectedTerms)
        setTrendingSince(keywordLookback)
        break
      }
    }
  }, [
    field,
    categorySelectedTerms,
    conceptSelectedTerms,
    domainSelectedTerms,
    entitySelectedTerms,
    keywordSelectedTerms,
    categoryLookback,
    conceptLookback,
    domainLookback,
    entityLookback,
    keywordLookback,
  ])

  //init the selected terms from visualizations storage
  useEffect(() => {
    setCategorySelectedTerms(categoryTerms.map((c) => c.term))
    setConceptSelectedTerms(conceptTerms)
    setDomainSelectedTerms(domainTerms)
    setEntitySelectedTerms(entityTerms)
    setKeywordSelectedTerms(keywordTerms)
  }, [isOpen, categoryTerms, conceptTerms, domainTerms, entityTerms, keywordTerms])

  const trendingTermsPayload = useMemo(
    () => ({
      filters,
      field,
      entityType,
      lookback: trendingSince,
      language: englishLanguage ? 'en' : 'all',
    }),
    [filters, field, entityType, trendingSince, englishLanguage]
  )
  const [data, loadingTrendingTerms] = useTrendingTerms(trendingTermsPayload)

  const handleAddTermClick = (item) => {
    const newValue = [...selectedTerms, item]
    if (selectedTerms.length < 10) {
      updateSelectedTerms(newValue)
    }
  }

  const handleRemoveTermClick = (item) => {
    const newValue = selectedTerms.filter((f) => f.term !== item.term)
    updateSelectedTerms(newValue)
  }

  const updateSelectedTerms = (newValue) => {
    setSelectedTerms(newValue)
    switch (field) {
      case TYPES.CATEGORIES: {
        setCategorySelectedTerms(newValue)
        break
      }
      case TYPES.CONCEPTS: {
        setConceptSelectedTerms(newValue)
        break
      }
      case TYPES.DOMAINS: {
        setDomainSelectedTerms(newValue)
        break
      }
      case TYPES.ENTITIES: {
        setEntitySelectedTerms(newValue)
        break
      }
      case TYPES.KEYWORDS: {
        setKeywordSelectedTerms(newValue)
        break
      }
    }
  }

  const handleAddMoreTerms = (value) => {
    handleAddTermClick({ term: value })
    setMoreTerms('')
  }

  const renderChipsPlaceholders = () => (
    <>
      <RectLoader style={{ marginRight: 10, marginBottom: 10 }} width={120} height={40} />
      <RectLoader style={{ marginRight: 10, marginBottom: 10 }} width={120} height={40} />
      <RectLoader style={{ marginRight: 10, marginBottom: 10 }} width={120} height={40} />
      <RectLoader style={{ marginRight: 10, marginBottom: 10 }} width={120} height={40} />
      <RectLoader style={{ marginRight: 10, marginBottom: 10 }} width={120} height={40} />
      <RectLoader style={{ marginRight: 10, marginBottom: 10 }} width={120} height={40} />
      <RectLoader style={{ marginRight: 10, marginBottom: 10 }} width={120} height={40} />
      <RectLoader style={{ marginRight: 10, marginBottom: 10 }} width={120} height={40} />
      <RectLoader style={{ marginRight: 10, marginBottom: 10 }} width={120} height={40} />
      <RectLoader style={{ marginRight: 10, marginBottom: 10 }} width={120} height={40} />
    </>
  )

  const renderOtherFieldsUI = () => (
    <div className="add-terms-modal__main-column">
      <div className="add-terms-modal__selectors-container">
        <If condition={field === TYPES.ENTITIES}>
          <Select
            style={{ flex: 1 }}
            label="Entity Type"
            options={[
              { label: 'All Types', value: ENTITY_TYPES.ALL_TYPES },
              { label: 'Person', value: ENTITY_TYPES.PERSON },
              { label: 'Organization', value: ENTITY_TYPES.ORGANIZATION },
              { label: 'Location', value: ENTITY_TYPES.LOCATION },
              { label: 'Number', value: ENTITY_TYPES.NUMBER },
              { label: 'Date', value: ENTITY_TYPES.DATE },
              { label: 'Company', value: ENTITY_TYPES.COMPANY },
              { label: 'Quantity', value: ENTITY_TYPES.QUANTITY },
              { label: 'Job Title', value: ENTITY_TYPES.JOB_TITLE },
              { label: 'Facility', value: ENTITY_TYPES.FACILITY },
              { label: 'Duration', value: ENTITY_TYPES.DURATION },
            ]}
            value={entityType}
            onChange={({ value }) => setEntityType(value)}
            useReactSelect
          />
        </If>
        <LookbackSelectStored
          style={{ flex: 1, marginLeft: 10 }}
          label="Trending Since"
          visualizationsPath={`trends.${field}`}
          lookback={trendingSince}
          onLookbackChange={(value) => setTrendingSince(value)}
        />
        <CheckBox
          className="add-terms-modal__checkbox-language"
          classes={{
            label: classNames('add-terms-modal__checkbox-language-label', {
              'add-terms-modal__checkbox-language-label-checked': englishLanguage,
            }),
          }}
          checked={englishLanguage}
          onChange={(e) => setEnglishLanguage(e.target.checked)}
          label="Only show terms from English language websites"
        />
      </div>
      <div className="add-terms-modal__main-row">
        <div className="add-terms-modal__selected">
          <div className="add-terms-modal__selected-header">
            <div className="add-terms-modal__selected-header-title">
              <h3 className="add-terms-modal__selected-header-title-main">
                Selected {capitalizeFirstLetter(field)}
              </h3>
              <span className="add-terms-modal__selected-subtitle">Choose up to 10 {field}</span>
            </div>
            <div>
              <Button
                theme={BUTTON_THEME.GHOST}
                className="add-terms-modal__selected-header-clear-all"
                onClick={() => setSelectedTerms([])}
              >
                Clear all
              </Button>
            </div>
          </div>
          <div className="add-terms-modal__selected-chips">
            <For each="item" of={selectedTerms}>
              <div onClick={() => handleRemoveTermClick(item)}>
                <Chip
                  key={item.term}
                  className="add-terms-modal__chip"
                  iconStyle={{ width: 16, height: 16, fill: UIKIT_COLORS.ds_gray }}
                  value={`${item.term}${item.trend ? ` (${twoNonZeroDecimals(item.trend)})` : ''}`}
                />
              </div>
            </For>
          </div>
          <div style={{ flex: 1 }} />
          <div className="add-terms-modal__more-terms-container">
            <Input
              label={`Add more ${field}`}
              value={moreTerms}
              onChange={(value) => {
                setMoreTerms(value)
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  handleAddMoreTerms(event.target.value)
                }
              }}
            />
          </div>
        </div>
        <div className="add-terms-modal__selection">
          <If condition={selectedTerms.length === 10}>
            <span className="add-terms-modal__remove-info">
              <Icon type="fill" icon="info" color="red" /> Remove a selected term in order to
              include other
            </span>
          </If>
          <Collapsible
            classes={{
              root: 'add-terms-modal__list',
              header: 'add-terms-modal__list-header',
              content: 'add-terms-modal__list-content add-terms-modal__list-content--wrap',
            }}
            title="Popular and Trending"
            showOpenButton={false}
            showDeleteAllButton={false}
            customContent={<div />} //used empty div only to occupy the second space allowing the title and open button to be spaced-between
            forcedState={Math.random()} //triggers a recalculation of contents size every render
          >
            <If condition={loadingTrendingTerms}>{renderChipsPlaceholders()}</If>
            <If condition={!loadingTrendingTerms}>
              <For
                each="item"
                of={
                  data?.popularAndTrending?.filter(
                    (f, index) => !selectedTerms.find((d) => f.term === d.term) && index < 25
                  ) || []
                }
              >
                <div onClick={() => handleAddTermClick(item)}>
                  <Chip
                    key={item.term}
                    className="add-terms-modal__chip"
                    iconStyle={{ width: 16, height: 16, fill: UIKIT_COLORS.ds_gray }}
                    value={`${item.term} (${twoNonZeroDecimals(item.trend)}x)`}
                    hasAddIcon
                  />
                </div>
              </For>
            </If>
          </Collapsible>
          <Collapsible
            classes={{
              root: 'add-terms-modal__list',
              header: 'add-terms-modal__list-header',
              content: 'add-terms-modal__list-content add-terms-modal__list-content--wrap',
            }}
            title="Trending"
            showOpenButton={false}
            showDeleteAllButton={false}
            customContent={<div />} //used empty div only to occupy the second space allowing the title and open button to be spaced-between
            forcedState={Math.random()} //triggers a recalculation of contents size every render
          >
            <If condition={loadingTrendingTerms}>{renderChipsPlaceholders()}</If>
            <If condition={!loadingTrendingTerms}>
              <For
                each="item"
                of={
                  data?.trending?.filter(
                    (f, index) => !selectedTerms.find((d) => f.term === d.term) && index < 25
                  ) || []
                }
              >
                <div onClick={() => handleAddTermClick(item)}>
                  <Chip
                    key={item.term}
                    className="add-terms-modal__chip"
                    iconStyle={{ width: 16, height: 16, fill: UIKIT_COLORS.ds_gray }}
                    containerCustomStyle={{
                      borderColor: 'transparent',
                      backgroundColor: '#EBEBEB',
                      color: '#3d3d3d',
                    }}
                    value={`${item.term} (${twoNonZeroDecimals(item.trend)}x)`}
                    hasAddIcon
                  />
                </div>
              </For>
            </If>
          </Collapsible>
          <Collapsible
            classes={{
              root: 'add-terms-modal__list',
              header: 'add-terms-modal__list-header',
              content: 'add-terms-modal__list-content add-terms-modal__list-content--wrap',
            }}
            title="Popular"
            showOpenButton={false}
            showDeleteAllButton={false}
            customContent={<div />} //used empty div only to occupy the second space allowing the title and open button to be spaced-between
            forcedState={Math.random()} //triggers a recalculation of contents size every render
          >
            <If condition={loadingTrendingTerms}>{renderChipsPlaceholders()}</If>
            <If condition={!loadingTrendingTerms}>
              <For
                each="item"
                of={
                  data?.popular?.filter(
                    (f, index) => !selectedTerms.find((d) => f.term === d.term) && index < 25
                  ) || []
                }
              >
                <div onClick={() => handleAddTermClick(item)}>
                  <Chip
                    key={item.term}
                    className="add-terms-modal__chip"
                    iconStyle={{ width: 16, height: 16, fill: UIKIT_COLORS.ds_gray }}
                    containerCustomStyle={{
                      borderColor: 'transparent',
                      backgroundColor: '#EBEBEB',
                      color: '#3d3d3d',
                    }}
                    value={`${item.term} (${twoNonZeroDecimals(item.trend)}x)`}
                    onClick={() => handleAddTermClick(item)}
                    onButtonClick={() => handleAddTermClick(item)}
                    hasAddIcon
                  />
                </div>
              </For>
            </If>
          </Collapsible>
        </div>
      </div>
      <div className="add-terms-modal__footer">
        <span className="add-terms-modal__footer-selected-total">
          {selectedTerms.length} selected total
        </span>
        <Button
          className="add-terms-modal__save-changes"
          disabled={selectedTerms.length === 0}
          onClick={(e) =>
            onSaveChangesClick(e, {
              conceptTerms: conceptSelectedTerms,
              domainTerms: domainSelectedTerms,
              entityTerms: entitySelectedTerms,
              keywordTerms: keywordSelectedTerms,
            })
          }
        >
          Save Changes
        </Button>
      </div>
    </div>
  )

  const level2CategoryItems = (level2Category) =>
    extractedCategoryTree
      ?.find((c) => c.value === selectedLevel1Category)
      ?.children?.find((c) => c.value === level2Category.value)?.children || []

  const countCategorySelectedChildren = (categoryValue) =>
    selectedTerms.filter((f) => typeof f === 'string' && f.startsWith(categoryValue)).length

  const level3CategoryFoundSearch = (level2Category) =>
    !!level2CategoryItems(level2Category).find((f) =>
      f.label.toLowerCase().includes(categoriesSearch.toLowerCase())
    )

  const renderCategoryFieldUI = () => (
    <div className="add-terms-modal__category-column">
      <div className="add-terms-modal__category">
        <section className="add-terms-modal__category-level1">
          <For each="level1Category" of={extractedCategoryTree}>
            <div
              key={level1Category.value}
              className={classNames('add-terms-modal__category-level1-item', {
                'add-terms-modal__category-level1-item--selected':
                  level1Category.value === selectedLevel1Category,
              })}
              onClick={() => setSelectedLevel1Category(level1Category.value)}
            >
              <span className="add-terms-modal__category-level1-item-label">
                {level1Category.label}
              </span>
              <If condition={countCategorySelectedChildren(level1Category.value)}>
                <div className="add-terms-modal__category-level1-item-bubble">
                  {countCategorySelectedChildren(level1Category.value)}
                </div>
              </If>
            </div>
          </For>
        </section>
        <section className="add-terms-modal__category-level2">
          <Input
            theme="search"
            placeholder="Quick Search"
            value={categoriesSearch}
            onChange={(value) => setCategoriesSearch(value)}
          />
          <CheckBox
            classes={{
              root: 'add-terms-modal__all-checkbox-root',
              label: 'add-terms-modal__all-checkbox-label',
            }}
            checked={selectedTerms.includes(selectedLevel1Category)}
            onChange={(e) => {
              updateSelectedTerms(
                e.target.checked
                  ? [...selectedTerms, selectedLevel1Category]
                  : selectedTerms.filter(
                      (level1CategoryValue) => level1CategoryValue !== selectedLevel1Category
                    )
              )
            }}
            label={extractedCategoryTree.find((f) => f.value === selectedLevel1Category)?.label}
          />
          <For
            each="level2Category"
            of={
              extractedCategoryTree?.find((c) => c.value === selectedLevel1Category)?.children || []
            }
          >
            <If
              condition={
                level2Category.label.toLowerCase().includes(categoriesSearch.toLowerCase()) ||
                level3CategoryFoundSearch(level2Category)
              }
            >
              <Collapsible
                key={level2Category.value}
                classes={{
                  root: 'add-terms-modal__list',
                  header: classNames('add-terms-modal__list-header', {
                    'add-terms-modal__list-header--empty': !extractedCategoryTree
                      ?.find((c) => c.value === selectedLevel1Category)
                      ?.children?.find((c) => c.value === level2Category.value)?.children,
                  }),
                  content: 'add-terms-modal__list-content',
                }}
                title={
                  <div>
                    <CheckBox
                      classes={{
                        label: classNames('add-terms-modal__list-checkbox-label', {
                          'add-terms-modal__list-checkbox-label--active': selectedTerms.includes(
                            level2Category.value
                          ),
                        }),
                      }}
                      label={level2Category.label}
                      checked={selectedTerms.includes(level2Category.value)}
                      onChange={(e) => {
                        updateSelectedTerms(
                          e.target.checked
                            ? Array.from(new Set([...selectedTerms, level2Category.value]))
                            : selectedTerms.filter(
                                (level2CategoryValue) =>
                                  level2CategoryValue !== level2Category.value
                              )
                        )
                      }}
                    />
                  </div>
                }
                showOpenButton={false}
                showDeleteAllButton={false}
                customContent={<div />} //used empty div only to occupy the second space allowing the title and open button to be spaced-between
                forcedState={level3CategoryFoundSearch(level2Category) && categoriesSearch !== ''}
              >
                <For each="level3Category" of={level2CategoryItems(level2Category)}>
                  <If
                    condition={level3Category.label
                      .toLowerCase()
                      .includes(categoriesSearch.toLowerCase())}
                  >
                    <div key={level3Category.value}>
                      <CheckBox
                        classes={{
                          label: classNames(
                            'add-terms-modal__list-checkbox-label',
                            'add-terms-modal__list-checkbox-label--level3',
                            {
                              'add-terms-modal__list-checkbox-label--active': selectedTerms.includes(
                                level3Category.value
                              ),
                            }
                          ),
                        }}
                        label={level3Category.label}
                        checked={selectedTerms.includes(level3Category.value)}
                        onChange={(e) =>
                          updateSelectedTerms(
                            e.target.checked
                              ? [...selectedTerms, level3Category.value]
                              : selectedTerms.filter((f) => f !== level3Category.value)
                          )
                        }
                      />
                    </div>
                  </If>
                </For>
              </Collapsible>
            </If>
          </For>
        </section>
      </div>
      <div className="add-terms-modal__category-footer">
        <span>{selectedTerms.length} Selected total</span>
        <Button
          disabled={selectedTerms.length === 0}
          onClick={(e) =>
            onSaveChangesClick(e, {
              conceptTerms: conceptSelectedTerms,
              domainTerms: domainSelectedTerms,
              entityTerms: entitySelectedTerms,
              keywordTerms: keywordSelectedTerms,
              categoryTerms: selectedTerms
                .filter((f) => typeof f === 'string')
                .map((c) => ({ term: c })),
            })
          }
        >
          Save Changes
        </Button>
      </div>
    </div>
  )

  return (
    <>
      <Select
        className="add-terms-modal__header-select"
        classes={{
          control: 'add-terms-modal__header-select-content',
        }}
        options={[
          { label: 'Entities', value: TYPES.ENTITIES },
          { label: 'Keywords', value: TYPES.KEYWORDS },
          { label: 'Domains', value: TYPES.DOMAINS },
          { label: 'Categories', value: TYPES.CATEGORIES },
          { label: 'Concepts', value: TYPES.CONCEPTS },
        ]}
        value={field}
        onChange={({ value }) => onFieldChange(value)}
        useReactSelect
      />

      <If condition={field !== TYPES.CATEGORIES}>{renderOtherFieldsUI()}</If>
      <If condition={field === TYPES.CATEGORIES}>{renderCategoryFieldUI()}</If>
    </>
  )
}

export default AddTermsModal
